import React, { useContext, useEffect, useState } from "react";
import { userRepo } from "../../repo/user-repo.ts";
import { UserData } from "../../entry/server/user.ts";
import { AppContext } from "../../context/app-context.tsx";

const inputClass =
  "w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400";

export type SeasonWinnerFormState = {
  season_number: string;
  user_id: string;
  rank: string;
  level: string;
  score: string;
  prize: string;
};

type Props = {
  form: SeasonWinnerFormState;
  username?: string;
  onChange: (field: keyof SeasonWinnerFormState, value: string) => void;
};

export default function SeasonWinnerFormFields({ form, username, onChange }: Props) {
  const { setMessage } = useContext(AppContext);
  const [userQuery, setUserQuery] = useState(username ?? "");
  const [userResults, setUserResults] = useState<UserData[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUsername, setSelectedUsername] = useState(username ?? "");

  useEffect(() => {
    if (!username) return;
    setSelectedUsername(username);
    setUserQuery(username);
  }, [username]);

  const onSearchUser = async () => {
    const query = userQuery.trim();
    if (!query) {
      setUserResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await userRepo.getUsers(1, 10, query);
      if (!response.status || !response.data) {
        setMessage({ type: "error", message: response.message || "جستجوی کاربر ناموفق بود." });
        setUserResults([]);
        return;
      }
      setUserResults(response.data);
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      setUserResults([]);
    } finally {
      setSearching(false);
    }
  };

  const onSelectUser = (user: UserData) => {
    onChange("user_id", String(user.id));
    setSelectedUsername(user.username);
    setUserResults([]);
    setUserQuery(user.username);
  };

  const shownUsername = selectedUsername || username;

  return (
    <>
      <label className="space-y-2">
        <span className="text-sm text-slate-300">شماره فصل</span>
        <input
          type="number"
          min={1}
          value={form.season_number}
          onChange={(e) => onChange("season_number", e.target.value)}
          className={inputClass}
          placeholder="۱"
        />
      </label>
      <label className="space-y-2">
        <span className="text-sm text-slate-300">رتبه</span>
        <input
          type="number"
          min={1}
          value={form.rank}
          onChange={(e) => onChange("rank", e.target.value)}
          className={inputClass}
          placeholder="۱"
        />
      </label>
      <label className="space-y-2">
        <span className="text-sm text-slate-300">سطح</span>
        <input
          type="number"
          min={0}
          value={form.level}
          onChange={(e) => onChange("level", e.target.value)}
          className={inputClass}
          placeholder="۰"
        />
      </label>
      <label className="space-y-2">
        <span className="text-sm text-slate-300">امتیاز</span>
        <input
          type="number"
          min={0}
          value={form.score}
          onChange={(e) => onChange("score", e.target.value)}
          className={inputClass}
          placeholder="۰"
        />
      </label>
      <div className="space-y-2 md:col-span-2">
        <span className="text-sm text-slate-300">کاربر</span>
        <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
          <input
            value={userQuery}
            onChange={(e) => setUserQuery(e.target.value)}
            className={inputClass}
            placeholder="جستجو با نام کاربری..."
          />
          <button
            type="button"
            onClick={() => void onSearchUser()}
            disabled={searching}
            className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300 disabled:opacity-50"
          >
            {searching ? "در حال جستجو…" : "جستجو"}
          </button>
        </div>
        {userResults.length > 0 ? (
          <ul className="overflow-hidden rounded-lg border border-slate-700 bg-slate-950">
            {userResults.map((user) => (
              <li key={user.id}>
                <button
                  type="button"
                  onClick={() => onSelectUser(user)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-right text-sm text-slate-200 transition hover:bg-slate-800"
                >
                  <span>{user.username}</span>
                  <span className="text-slate-400">{user.name || `#${user.id}`}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <label className="block space-y-2">
          <span className="text-xs text-slate-400">شناسه کاربر</span>
          <input
            type="number"
            min={1}
            value={form.user_id}
            onChange={(e) => {
              onChange("user_id", e.target.value);
              setSelectedUsername("");
            }}
            className={inputClass}
            placeholder="شناسه کاربر"
          />
        </label>
        {shownUsername ? <p className="text-xs text-indigo-200">کاربر انتخاب‌شده: {shownUsername}</p> : null}
      </div>
      <label className="space-y-2 md:col-span-2">
        <span className="text-sm text-slate-300">جایزه</span>
        <textarea
          value={form.prize}
          onChange={(e) => onChange("prize", e.target.value)}
          maxLength={512}
          rows={3}
          className={inputClass}
          placeholder="شرح جایزه را وارد کنید..."
        />
      </label>
    </>
  );
}

export function isSeasonWinnerFormValid(form: SeasonWinnerFormState) {
  const season = Number(form.season_number);
  const userId = Number(form.user_id);
  const rank = Number(form.rank);
  const level = Number(form.level);
  const score = Number(form.score);
  const prize = form.prize.trim();

  return (
    Number.isInteger(season) &&
    season > 0 &&
    Number.isInteger(userId) &&
    userId > 0 &&
    Number.isInteger(rank) &&
    rank > 0 &&
    Number.isInteger(level) &&
    level >= 0 &&
    Number.isInteger(score) &&
    score >= 0 &&
    prize.length >= 1 &&
    prize.length <= 512
  );
}

export function emptySeasonWinnerForm(): SeasonWinnerFormState {
  return {
    season_number: "",
    user_id: "",
    rank: "",
    level: "",
    score: "",
    prize: "",
  };
}

export function toSeasonWinnerPayload(form: SeasonWinnerFormState) {
  return {
    season_number: Number(form.season_number),
    user_id: Number(form.user_id),
    rank: Number(form.rank),
    level: Number(form.level),
    score: Number(form.score),
    prize: form.prize.trim(),
  };
}
